import { BasePropertyDTO } from 'business/models/Models';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';

const BATCH_TIME_WINDOW_MS = 50;
export interface ISystemPropertyKeyCache {
  get(name: string): Promise<BasePropertyDTO | null>;
  clear(name?: string): void;
}
class SystemPropertiesCache implements ISystemPropertyKeyCache {
  private cache: Record<string, BasePropertyDTO> = {};
  private pending: Record<string, Promise<BasePropertyDTO | null>> = {};

  private batchQueue = new Set<string>();
  private batchResolvers: Record<
    string,
    {
      resolve: (value: BasePropertyDTO | null) => void;
      reject: (reason?: any) => void;
    }
  > = {};

  private batchTimer: NodeJS.Timeout | null = null;

  async get(name: string): Promise<BasePropertyDTO | null> {
    if (this.cache[name]) {
      return this.cache[name];
    }

    if (this.pending[name]) {
      return this.pending[name];
    }

    const promise = new Promise<BasePropertyDTO | null>((resolve, reject) => {
      this.batchQueue.add(name);

      this.batchResolvers[name] = {
        resolve,
        reject,
      };

      // start batch timer
      if (!this.batchTimer) {
        this.batchTimer = setTimeout(() => {
          this.flushBatch();
        }, BATCH_TIME_WINDOW_MS);
      }
    });

    this.pending[name] = promise;

    return promise;
  }

  private async flushBatch() {
    const names = Array.from(this.batchQueue);

    this.batchQueue.clear();
    this.batchTimer = null;

    if (names.length === 0) {
      return;
    }

    try {
      const globalPrefService = new GlobalPreferencesService();
      const results = await globalPrefService.getProperties(names);

      for (const item of results) {
        this.cache[item.name] = item;

        this.batchResolvers[item.name]?.resolve(item);

        delete this.pending[item.name];
        delete this.batchResolvers[item.name];
      }

      for (const name of names) {
        if (!this.cache[name]) {
          this.batchResolvers[name]?.resolve(null);

          delete this.pending[name];
          delete this.batchResolvers[name];
        }
      }
    } catch (err) {
      console.error(err);

      for (const name of names) {
        this.batchResolvers[name]?.reject(err);

        delete this.pending[name];
        delete this.batchResolvers[name];
      }
    }
  }

  clear(name?: string) {
    if (name) {
      delete this.cache[name];
    } else {
      this.cache = {};
    }
  }
}

export const systemPropertiesCache = new SystemPropertiesCache();
